import { useState } from "react";
import { GraduationCap, Edit2, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCandidateEducation,
  useDeleteEducation,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { EducationFormModal } from "@/pages/profile/employee/sections/components/EducationFormModal";
import type { EducationDetail, ProfileDetail } from "@/interface/profile";

export function EducationSection({
  profileId,
}: {
  profileId?: number | string;
  profile?: ProfileDetail;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEdu, setSelectedEdu] = useState<EducationDetail | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const {
    data: candidateEduRes,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateEducation(profileId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteEducation();

  const educationList: EducationDetail[] =
    candidateEduRes?.pages?.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleOpenAdd = () => {
    setSelectedEdu(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (edu: EducationDetail) => {
    setSelectedEdu(edu);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-primary">Education</h2>
          <p className="text-xs text-slate-500">Candidate academic records</p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Education
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4 max-h-[500px]"
        >
          <div className="space-y-4">
            {educationList.map((edu: EducationDetail) => {
              const degreeTitle =
                edu.course_name || edu.degree?.degree_name || "";
              const institutionName = edu.institution || "Institution N/A";
              const startYr = edu.start_year;
              const endYr = edu.end_year;
              const yearDisplay =
                startYr || endYr
                  ? startYr && endYr
                    ? `${startYr} - ${endYr}`
                    : startYr
                      ? `${startYr} - Present`
                      : `${endYr}`
                  : "";

              const gradeDisplay = edu.grade_cgpa ?? "";

              return (
                <div
                  key={edu.id}
                  className="flex justify-between items-center p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <GraduationCap className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                        {edu.degree?.degree_name || ''} {edu.course_name ? (` - ${edu.course_name}`) : ''}
                      </h4>
                      <p className="text-xs text-slate-500">
                        {institutionName}
                      </p>
                      <div className="flex gap-4 mt-2 text-xs font-medium text-slate-400">
                        {yearDisplay && <span>{yearDisplay}</span>}
                        {gradeDisplay && (
                          <span>
                            {typeof gradeDisplay === "string" &&
                              gradeDisplay.toLowerCase().includes("grade")
                              ? gradeDisplay
                              : `Grade: ${gradeDisplay}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenEdit(edu)}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                    >
                      <Edit2 className="h-4 w-4 text-slate-400" />
                    </button>
                    <button
                      onClick={() => setDeleteTargetId(String(edu.id))}
                      className="p-2 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-rose-400" />
                    </button>
                  </div>
                </div>
              );
            })}

            {educationList.length === 0 && (
              <div className="text-center p-8 text-slate-500 text-sm">
                No education records added yet.
              </div>
            )}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more education records...
              </span>
            </div>
          )}
        </div>
      )}

      <EducationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedEdu}
        profileId={profileId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Education"
        description="Are you sure you want to remove this education record?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
